import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { alertApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';
import {
  PipelineConfig,
  PipelinePrResult,
  groupTagsFromOwnership,
  teamTagsFromOwnership,
  validatePipelineConfig,
  withDescriptionFallback,
} from '@internal/backstage-plugin-sql-pipeline-common';

import { sqlPipelineApiRef } from '../api/SqlPipelineApi';
import { emptyPipelineConfig, PipelineForm } from './PipelineForm';
import { PipelinePreview } from './PipelinePreview';
import { PrCreatedDialog } from './PrCreatedDialog';
import { TicketField, useTicket } from './TicketField';


const useStyles = makeStyles(theme => ({
  notice: {
    padding: theme.spacing(1.5, 2),
    borderRadius: 4,
    border: `1px solid ${theme.palette.info.main}`,
    backgroundColor: theme.palette.background.default,
  },
}));

/**
 * 파이프라인 생성 다이얼로그.
 *
 * scaffolder 템플릿을 대체한다. 새 규약은 파일 3 개를 만들고 그 내용이 서로
 * 맞물리므로(스케줄 주기가 삭제 쿼리의 날짜 식을 정하고, 생성 컬럼이 INSERT
 * 컬럼 목록과 같아야 한다) JSON Schema 폼으로는 교차 검증을 표현할 수 없다.
 * 같은 이유로 수정 폼과 **같은 컴포넌트**를 쓴다 — 두 벌이면 한쪽에만 검증이
 * 들어가는 결함군이 생긴다.
 */
export const CreatePipelineDialog = (props: {
  open: boolean;
  onClose: () => void;
  /** SQL 콘솔에서 넘어온 초기 SQL. */
  initialSql?: string;
}) => {
  const { open, onClose, initialSql } = props;
  const classes = useStyles();
  const api = useApi(sqlPipelineApiRef);
  const alertApi = useApi(alertApiRef);
  const identityApi = useApi(identityApiRef);

  // 열릴 때마다 빈 폼에서 시작하고, 담당자 정보는 로그인 신원에서 채운다 —
  // 가이드가 `owner`/`email` 을 사람이 고치게 두는 부분이고, 「바꾸세요」 주석을
  // 남겨 두면 예시 값이 그대로 배포된다.
  const [config, setConfig] = useState<PipelineConfig | null>(null);
  /**
   * 고를 수 있는 팀 = **내가 속한 Keycloak 그룹.** 위키의 「내 팀 폴더 안에서만
   * 작업하세요」 가 폼에서 강제된다.
   *
   * ⚠️ 이것은 UX 이고 인가가 아니다 — 드롭다운을 좁혀도 API 는 열려 있으므로
   * 서버가 `team_tag ∈ 호출자의 그룹` 을 검사해야 한다(별도 티켓).
   */
  const [availableTeams, setAvailableTeams] = useState<string[]>([]);
  /**
   * 교집합 전의 내 그룹. `availableTeams` 가 빈 이유를 가르는 데만 쓴다 —
   * 그룹조차 없는 것과 `TEAM_TAGS` 에 없는 그룹뿐인 것은 요청 내용이 다르다.
   */
  const [ownedGroups, setOwnedGroups] = useState<string[]>([]);
  /**
   * 폼 → 미리보기 2 단계. 미리보기는 **검증을 통과한 설정에만** 보여준다 —
   * 못 저장할 설정의 파일을 보여주면 사용자가 그것이 결과물이라고 믿는다.
   */
  const [step, setStep] = useState<'form' | 'preview'>('form');
  useEffect(() => {
    if (!open) return undefined;
    setStep('form');
    let cancelled = false;
    (async () => {
      const identity = await identityApi.getBackstageIdentity();
      const { userEntityRef } = identity;
      const owner = userEntityRef.replace(/^user:[^/]*\//, '');
      if (!cancelled) {
        setAvailableTeams(teamTagsFromOwnership(identity.ownershipEntityRefs));
        setOwnedGroups(groupTagsFromOwnership(identity.ownershipEntityRefs));
      }
      const profile = await identityApi.getProfileInfo();
      const base = emptyPipelineConfig({
        owner,
        ownerEmail: profile.email ?? `${owner}@wesang.com`,
        now: new Date(),
      });
      // SQL 콘솔에서 넘어온 SQL 은 첫 테이블의 「넣기」 에 넣는다. 머리말
      // (INSERT INTO ...)은 사용자가 채워야 하므로 기본 형태를 덮어쓴다.
      if (!cancelled) {
        setConfig(
          initialSql
            ? {
                ...base,
                groups: base.groups.map((g, i) =>
                  i === 0
                    ? { ...g, tasks: { ...g.tasks, insert: { sql: initialSql } } }
                    : g,
                ),
              }
            : base,
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, identityApi, initialSql]);
  const ticketState = useTicket();
  const { ticket } = ticketState;
  const [saving, setSaving] = useState(false);
  const [issues, setIssues] = useState<Record<string, string>>({});
  const [result, setResult] = useState<PipelinePrResult | null>(null);
  /**
   * 미리보기와 제출에 쓰는 설정 — 설명이 비면 티켓 링크로 채운 것이다.
   * 폼의 `config` 는 그대로 둔다. 폼으로 돌아가 티켓을 바꾸면 링크도 따라가야 한다.
   */
  const resolved = config ? withDescriptionFallback(config, ticket) : null;

  /** 검증을 통과하면 미리보기로 넘어간다. */
  const handlePreview = () => {
    if (!ticketState.ensureValid() || !resolved) return;
    const validation = validatePipelineConfig(resolved);
    if (!validation.ok) {
      setIssues(
        Object.fromEntries(validation.issues.map(i => [i.path, i.message])),
      );
      alertApi.post({
        message: `입력을 확인해주세요: ${validation.issues[0].message}`,
        severity: 'error',
      });
      return;
    }
    setIssues({});
    setStep('preview');
  };

  const handleCreate = async () => {
    if (!resolved) return;
    setSaving(true);
    try {
      const created = await api.createPipeline({
        ticketNumber: ticket,
        config: resolved,
      });
      setResult(created);
    } catch (e) {
      alertApi.post({
        message: (e as Error)?.message ?? '생성에 실패했습니다.',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (result) {
    return (
      <PrCreatedDialog
        open={open}
        onClose={() => {
          setResult(null);
          onClose();
        }}
        title="생성 PR 을 만들었습니다"
        result={result}
        outcome="정상적으로 반영되면 [내 파이프라인]에 추가됩니다. 평균 3분정도 소요됩니다."
      />
    );
  }

  return (
    // 바깥 클릭으로는 닫지 않는다 — 긴 폼을 채우다 창 밖을 한 번 누르면 입력이
    // 전부 사라졌다(사용자 지적). 닫기는 취소 버튼·Esc 로만 한다.
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') onClose();
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {step === 'form' ? '파이프라인 만들기' : '만들어질 파일 확인'}
      </DialogTitle>
      <DialogContent dividers>
        {step === 'preview' && resolved ? (
          <PipelinePreview config={resolved} />
        ) : (
          <>
            {/*
              안내 문구를 다이얼로그 맨 위에 둔다. 이 템플릿이 **무엇을 위한 것이
              아닌지**를 먼저 말하지 않으면, 템플릿으로 표현할 수 없는 요구(외부
              수집, DAG 간 의존, 알림 채널 변경)를 억지로 끼워 맞춘 DAG 이 올라오고
              그것을 되돌리는 비용이 훨씬 크다.
            */}
            <Box className={classes.notice} mb={2}>
              <Typography variant="body2">
                공통된 부분을 쉽게 build 하기 위한 template 입니다. 추가적인 로직이
                필요한 경우 <strong>#help_data</strong> 에서 <strong>@etl</strong> 을
                멘션하여 문의 바랍니다.
              </Typography>
            </Box>
            <TicketField
              state={ticketState}
              helperText="PR 브랜치명(feature/티켓번호)으로 사용됩니다"
              style={{ marginBottom: 24 }}
            />
            {config ? (
              <PipelineForm
                config={config}
                onChange={setConfig}
                errors={issues}
                mode="create"
                availableTeams={availableTeams}
                ownedGroups={ownedGroups}
              />
            ) : (
              <Box display="flex" alignItems="center" gridGap={8} py={4}>
                <CircularProgress size={20} />
                <Typography variant="body2">폼을 준비하는 중...</Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {step === 'preview' ? (
          <>
            <Button onClick={() => setStep('form')} disabled={saving}>
              돌아가서 수정
            </Button>
            <Box flex={1} />
            <Button onClick={onClose} disabled={saving}>
              취소
            </Button>
            <Button
              onClick={handleCreate}
              color="primary"
              variant="contained"
              disabled={saving}
              startIcon={
                saving ? <CircularProgress size={16} color="inherit" /> : undefined
              }
            >
              생성 PR 만들기
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>취소</Button>
            <Button
              onClick={handlePreview}
              color="primary"
              variant="contained"
              disabled={!config || availableTeams.length === 0}
            >
              만들어질 파일 보기
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
